/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onMounted,
  ref,
  shallowRef,
  triggerRef,
} from "vue";
import {
  LemmonGrid,
  Loading,
  useLocalization,
  type GridSchema,
} from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Supplier } from "./types";
import { AddSupplierModal, type NewSupplier } from "./AddSupplierModal";
import { SupplierItemsModal } from "./SupplierItemsModal";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// Who the shop buys from: outside suppliers and other Lemmon shops.
// See docs/plans/inventory_and_costing/05_purchasing.md (#2).
export const InventorySuppliersPage = defineComponent({
  name: "InventorySuppliersPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const data = shallowRef<Supplier[]>([]);
    const changedRowIds = ref<Set<number | string>>(new Set());
    const saving = ref(false);
    const showAdd = ref(false);
    const itemsFor = ref<Supplier | null>(null);

    const schema = computed(
      (): GridSchema<Supplier> => ({
        primaryKey: "id",
        columns: [
          {
            key: "name",
            header: t("inventory.suppliers.col_name"),
            type: "text",
            width: 220,
            frozen: true,
            editable: true,
            required: true,
            cellClass: "font-semibold",
          },
          {
            key: "email",
            header: t("inventory.suppliers.col_email"),
            type: "text",
            width: 200,
            editable: true,
          },
          {
            key: "phone",
            header: t("inventory.suppliers.col_phone"),
            type: "text",
            width: 150,
            editable: true,
          },
          {
            key: "vat_number",
            header: t("inventory.suppliers.col_vat"),
            type: "text",
            width: 150,
            editable: true,
          },
          {
            key: "supplier_restaurant_id",
            header: t("inventory.suppliers.col_type"),
            type: "readonly",
            width: 140,
            // renderCell, not formatter: the grid shows "—" for null values
            // without calling the formatter, and external suppliers are null.
            renderCell: (value) => (
              <span class="text-sm">
                {value
                  ? t("inventory.suppliers.type_lemmon")
                  : t("inventory.suppliers.type_external")}
              </span>
            ),
          },
          {
            key: "__items",
            header: t("inventory.suppliers.col_items"),
            type: "readonly",
            width: 120,
            renderCell: (_value, row) => (
              <button
                class="btn btn-ghost btn-xs w-full justify-start"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  itemsFor.value = row;
                }}
              >
                <span class="material-symbols-outlined text-sm">list_alt</span>
                {t("inventory.suppliers.col_items")}
              </button>
            ),
          },
          {
            key: "active",
            header: t("inventory.stock.col_active"),
            type: "boolean",
            width: 90,
            editable: true,
            bulkEditable: true,
          },
        ],
      }),
    );

    const load = async () => {
      data.value = (await trpc.purchasing.suppliers.query()) as Supplier[];
    };

    onMounted(async () => {
      try {
        await load();
      } catch {
        toast.showToast(t("inventory.suppliers.load_failed"), {
          type: "error",
        });
      } finally {
        loading.value = false;
      }
    });

    const handleCellChange = (
      rowId: string | number,
      field: string,
      value: any,
    ) => {
      const row = data.value.find((r) => r.id === rowId);
      if (!row) return;
      (row as any)[field] = value;
      changedRowIds.value = new Set([...changedRowIds.value, rowId]);
      triggerRef(data);
    };

    const handleSave = async () => {
      saving.value = true;
      try {
        await Promise.all(
          data.value
            .filter((r) => changedRowIds.value.has(r.id))
            .map((r) =>
              trpc.purchasing.updateSupplier.mutate({
                id: r.id,
                name: r.name,
                email: r.email || null,
                phone: r.phone || null,
                vat_number: r.vat_number || null,
                active: Boolean(r.active),
              }),
            ),
        );
        changedRowIds.value = new Set();
        await load();
        toast.showToast(t("inventory.suppliers.saved"), { type: "success" });
      } catch {
        toast.showToast(t("inventory.suppliers.save_failed"), {
          type: "error",
        });
      } finally {
        saving.value = false;
      }
    };

    const handleDeleteRows = async (rowIds: Set<string | number>) => {
      if (rowIds.size === 0) return;
      if (
        !confirm(
          t("inventory.suppliers.delete_confirm").replace(
            "{count}",
            String(rowIds.size),
          ),
        )
      )
        return;
      try {
        for (const id of rowIds)
          await trpc.purchasing.deleteSupplier.mutate({ id: id as number });
      } catch {
        toast.showToast(t("inventory.suppliers.save_failed"), {
          type: "error",
        });
      }
      await load();
    };

    const handleAdd = async (supplier: NewSupplier) => {
      saving.value = true;
      try {
        await trpc.purchasing.createSupplier.mutate(supplier);
        showAdd.value = false;
        await load();
        toast.showToast(t("inventory.suppliers.created"), { type: "success" });
      } catch {
        toast.showToast(t("inventory.suppliers.save_failed"), {
          type: "error",
        });
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("inventory.nav.suppliers")}
          subtitle={t("inventory.suppliers.subtitle")}
        >
          <div class="flex items-center gap-2">
            <button
              class="btn btn-neutral btn-sm"
              onClick={() => (showAdd.value = true)}
            >
              <span class="material-symbols-outlined text-base">add</span>
              {t("inventory.suppliers.add")}
            </button>
            {changedRowIds.value.size > 0 && (
              <>
                <button
                  class="btn btn-ghost btn-sm"
                  onClick={async () => {
                    changedRowIds.value = new Set();
                    await load();
                  }}
                >
                  {t("inventory.stock.discard")}
                </button>
                <button
                  class="btn btn-primary btn-sm"
                  disabled={saving.value}
                  onClick={handleSave}
                >
                  {t("general.words.save")}
                </button>
              </>
            )}
          </div>
        </SubpageHeader>

        <div class="flex-1 overflow-hidden p-3">
          {loading.value ? (
            <div class="flex h-full items-center justify-center">
              <Loading />
            </div>
          ) : data.value.length === 0 ? (
            <div class="flex h-full flex-col items-center justify-center gap-2 text-center">
              <span class="material-symbols-outlined text-5xl text-base-content/30">
                local_shipping
              </span>
              <p class="text-sm text-base-content/60">
                {t("inventory.suppliers.empty")}
              </p>
            </div>
          ) : (
            <LemmonGrid
              data={data.value}
              schema={schema.value}
              changedRowIds={changedRowIds.value}
              onCellChange={handleCellChange}
              onBulkChange={(
                ids: Set<string | number>,
                field: string,
                value: any,
              ) => {
                for (const id of ids) handleCellChange(id, field, value);
              }}
              onDeleteRows={handleDeleteRows}
            />
          )}
        </div>

        {showAdd.value && (
          <AddSupplierModal
            saving={saving.value}
            onClose={() => (showAdd.value = false)}
            onAdd={handleAdd}
          />
        )}
        {itemsFor.value && (
          <SupplierItemsModal
            supplier={itemsFor.value}
            onClose={() => (itemsFor.value = null)}
          />
        )}
      </div>
    );
  },
});
